import PropTypes from "prop-types";

import useTheme from "./useTheme";
import { THEME_DARK, THEME_LIGHT } from "./themeConstants";
import "./ThemeToggle.scss";

const SunIcon = () => (
    <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
    >
        <circle cx="12" cy="12" r="4" />
        <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
    </svg>
);

const MoonIcon = () => (
    <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
    >
        <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z" />
    </svg>
);

/**
 * Light/dark switch.
 *
 * One control with two ends rather than a list of choices: there are only two
 * themes, so a switch says everything a set of radios would and takes a third
 * of the room. The knob slides between the sun and the moon, and both ends stay
 * visible so it reads as "which of these two" rather than "on or off" — a bare
 * switch leaves you guessing whether checked means dark or means light.
 *
 * It is a `switch` to assistive tech, labelled "Dark theme", so its state is
 * announced as on/off against a name that says what "on" does.
 *
 * `menuItem` swaps in the role a `role="menu"` may contain — a menu accepts
 * menuitem-family children only, and a bare `switch` nested in one is dropped
 * by screen readers. The behaviour and the styling are identical either way.
 */
const ThemeToggle = ({ className = "", menuItem = false }) => {
    const { isDark, setTheme } = useTheme();

    // Space and Enter are what a switch responds to. Enter arrives as a click
    // on a <button> already; Space is handled by the browser too, so there is
    // no key handling to add — the element being a real button is what buys it.
    const flip = () => setTheme(isDark ? THEME_LIGHT : THEME_DARK);

    return (
        <button
            type="button"
            role={menuItem ? "menuitemcheckbox" : "switch"}
            aria-checked={isDark}
            aria-label="Dark theme"
            title={isDark ? "Switch to light theme" : "Switch to dark theme"}
            className={`theme-switch ${
                isDark ? "theme-switch-dark" : ""
            } ${className}`.trim()}
            onClick={flip}
        >
            {/* Both ends are drawn at all times; the knob below covers whichever
                one is active. Hidden from assistive tech because the button
                already carries the name and the state. */}
            <span className="theme-switch-end" aria-hidden="true">
                <SunIcon />
            </span>
            <span className="theme-switch-end" aria-hidden="true">
                <MoonIcon />
            </span>
            <span className="theme-switch-knob" aria-hidden="true">
                {isDark ? <MoonIcon /> : <SunIcon />}
            </span>
        </button>
    );
};

ThemeToggle.propTypes = {
    className: PropTypes.string,
    menuItem: PropTypes.bool,
};

export default ThemeToggle;
